/**
 * Rules of a shift's headcount (design 3c, "total + asignados"):
 * - `expected` is the total of people the shift expects.
 * - `assignedIDs` are the people assigned by name; they're part of that total.
 * - expected >= assigned always. The rest are free slots any collaborator of the
 *   qontact point can cover.
 */
export interface ShiftHeadcount {
    expected: number;
    assignedIDs: string[];
}

export const freeSlots = ({ expected, assignedIDs }: ShiftHeadcount): number =>
    Math.max(0, expected - assignedIDs.length);

/** -1 is disabled when every slot has a name */
export const canDecrement = ({ expected, assignedIDs }: ShiftHeadcount): boolean =>
    expected > assignedIDs.length;

export const decrement = (headcount: ShiftHeadcount): ShiftHeadcount =>
    canDecrement(headcount) ? { ...headcount, expected: headcount.expected - 1 } : headcount;

export const increment = (headcount: ShiftHeadcount): ShiftHeadcount => ({
    ...headcount,
    expected: headcount.expected + 1,
});

/**
 * Checks / unchecks a collaborator.
 * - Checking someone when there are no free slots raises the total (`bumped` = true).
 * - Unchecking someone lowers the total by 1: that person's slot goes away
 *   instead of becoming a free one (`lowered` = true, so the UI can say so).
 */
export const toggleAssigned = (
    headcount: ShiftHeadcount,
    userID: string
): { headcount: ShiftHeadcount; bumped: boolean; lowered: boolean } => {
    if (headcount.assignedIDs.includes(userID)) {
        const assignedIDs = headcount.assignedIDs.filter((id) => id !== userID);
        return {
            headcount: { expected: Math.max(assignedIDs.length, headcount.expected - 1), assignedIDs },
            bumped: false,
            lowered: true,
        };
    }
    const assignedIDs = [...headcount.assignedIDs, userID];
    const bumped = assignedIDs.length > headcount.expected;
    return {
        headcount: { expected: bumped ? assignedIDs.length : headcount.expected, assignedIDs },
        bumped,
        lowered: false,
    };
};

/**
 * "Seleccionar todos": assigns every given collaborator (raising the total if
 * needed) or, when all of them were already assigned, removes them (lowering
 * the total by the same amount, like unchecking one by one).
 */
export const toggleAssignedMany = (
    headcount: ShiftHeadcount,
    userIDs: string[]
): { headcount: ShiftHeadcount; bumped: boolean; lowered: boolean } => {
    const allAssigned = userIDs.length > 0 && userIDs.every((id) => headcount.assignedIDs.includes(id));
    if (allAssigned) {
        const removing = new Set(userIDs);
        const assignedIDs = headcount.assignedIDs.filter((id) => !removing.has(id));
        return {
            headcount: { expected: Math.max(assignedIDs.length, headcount.expected - userIDs.length), assignedIDs },
            bumped: false,
            lowered: true,
        };
    }
    const assignedIDs = [...headcount.assignedIDs, ...userIDs.filter((id) => !headcount.assignedIDs.includes(id))];
    const bumped = assignedIDs.length > headcount.expected;
    return {
        headcount: { expected: Math.max(headcount.expected, assignedIDs.length), assignedIDs },
        bumped,
        lowered: false,
    };
};

/**
 * Typed value of "Personas esperadas". Returns the new total when it's valid
 * (whole number >= assigned) or null to keep the previous one while typing.
 */
export const parseTypedExpected = (typed: string, assignedCount: number): number | null => {
    if (!/^\d+$/.test(typed.trim())) {
        return null;
    }
    const value = Number(typed);
    return value >= assignedCount ? value : null;
};

/** Summary line under the progress bar */
export const headcountHint = (headcount: ShiftHeadcount, bumped: boolean, lowered: boolean = false): string => {
    const assigned = headcount.assignedIDs.length;
    const open = freeSlots(headcount);
    if (headcount.expected === 0) {
        return "Sumá personas con + o asigná colaboradores de la lista.".localized();
    }
    if (lowered) {
        return "Bajamos el total a %s al quitar a la persona asignada.".localized(String(headcount.expected));
    }
    if (bumped) {
        return "Subimos el total a %s para incluir a todas las personas asignadas.".localized(String(headcount.expected));
    }
    if (assigned === 0) {
        return "Solo cantidad: cualquier colaborador del Qontact Point cubre los %s lugares.".localized(String(headcount.expected));
    }
    if (open === 0) {
        return "Todos los lugares están asignados por nombre. Usá + para sumar lugares sin asignar.".localized();
    }
    return "Esperamos a las %s personas asignadas y a %s más, sin definir.".localized(String(assigned), String(open));
};
